import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AncillaryItem, UpdateAncillaryStatusRequest } from '@app/shared/interfaces/ancillary.interface';
import { AncillaryService } from '@app/shared/services/ancillary.service';
import { DialogModule } from 'primeng/dialog';
import {
    AncillaryCreateEditComponent,
    AncillaryDialogMode,
    AncillaryFormModel
} from './ancillary-create-edit/ancillary-create-edit.component';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { MessageModule } from 'primeng/message';
import { AccountingReportService } from '@services/accounting-report-service.service';
import { Providers } from '@shared/interfaces/accounting-report.interface';
import { AlternativePackageContent } from '@app/shared/interfaces/ancillary.interface';

@Component({
    selector: 'app-ancillary-management',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        ProgressSpinnerModule,
        ConfirmPopupModule,
        DialogModule,
        ToastModule,
        AncillaryCreateEditComponent,
        SelectModule,
        FormsModule,
        PageHeaderComponent,
        InputTextModule,
        TabsModule,
        TextareaModule,
        MessageModule
    ],
    templateUrl: './ancillary-management.component.html',
    styleUrl: './ancillary-management.component.scss'
})
export class AncillaryManagementComponent implements OnInit {
    ancillaries: AncillaryItem[] = [];
    filteredAncillaries: AncillaryItem[] = [];
    loading = true;
    activeTab = 'bundle-content';

    statusOptions: SelectItem[] = [
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Forced', value: 'FORCED' },
        { label: 'Passive', value: 'PASSIVE' }
    ];
    typeOptions: SelectItem[] = [
        { label: 'All', value: null },
        { label: 'Bundle Content', value: 'BUNDLE_CONTENT' },
        { label: 'Provider Service Mapping', value: 'PROVIDER_SERVICE_MAPPING' }
    ];

    providerOptions: SelectItem[] = [];
    allProviderOptions: SelectItem[] = [];
    airlineOptions: SelectItem[] = [];
    fareOptions: SelectItem[] = [];
    categoryOptions: SelectItem[] = [];

    filters = {
        providerCode: null as string | null,
        airlineCode: null as string | null,
        fareTypeName: null as string | null,
        categoryName: null as string | null,
        mode: null as string | null,
        status: null as string | null,
        text: ''
    };

    ancillaryService = inject(AncillaryService);
    accountingReportService = inject(AccountingReportService);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    displayDialog = false;
    dialogMode: AncillaryDialogMode = 'add';
    dialogHeader = '';
    selected: AncillaryItem | null = null;
    dialogData: Partial<AncillaryFormModel> | null = null;
    selectedMessageProviderCode: string | null = null;
    messageLoading = false;
    messageSaving = false;
    alternativePackageContent: AlternativePackageContent | null = null;
    messageTranslations = this.createEmptyMessageTranslations();

    ngOnInit(): void {
        this.loadProviders();
        this.loadAncillaries();
    }

    loadProviders(): void {
        this.accountingReportService.getProviders().subscribe({
            next: (providers: Providers[]) => {
                this.allProviderOptions = [...providers]
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .map((provider) => ({
                        label: provider.explanation ? `${provider.code} - ${provider.explanation}` : provider.code,
                        value: provider.code
                    }));
                this.cd.detectChanges();
            }
        });
    }

    loadAncillaries(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.ancillaryService.getAncillaries().subscribe({
            next: (data) => {
                this.ancillaries = data.map((item) => ({
                    ...item,
                    mode: item.mode ?? 'PROVIDER_SERVICE_MAPPING',
                    uiDisplayText: item.titlesByLanguage ? Object.values(item.titlesByLanguage).filter(Boolean).join(' ') : ''
                }));
                this.buildFilterOptions();
                this.applyFilters();
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    openAdd(): void {
        this.dialogMode = 'add';
        this.dialogHeader = 'Add Ancillary Content';
        this.selected = null;
        this.dialogData = null;
        this.displayDialog = true;
    }

    onEdit(row: AncillaryItem): void {
        this.dialogMode = 'edit';
        this.dialogHeader = row.mode === 'BUNDLE_CONTENT' ? 'Edit Bundle Content' : 'Edit Provider Service Mapping';
        this.selected = row;
        this.dialogData = this.mapToDialogData(row);
        this.displayDialog = true;
    }

    confirmDelete(event: Event, row: AncillaryItem): void {
        this.confirmationService.confirm({
            target: event.target as HTMLElement,
            message: 'Are you sure you want to delete this item?',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: { severity: 'danger' },
            accept: () => this.onDelete(row)
        });
    }

    onDelete(row: AncillaryItem): void {
        if (!row?.id) return;
        this.ancillaryService.deleteAncillary(row.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been deleted successfully.' });
                this.loadAncillaries();
            }
        });
    }

    onDialogCancel(): void {
        this.displayDialog = false;
    }

    onDialogSave(model: AncillaryFormModel): void {
        const payload = {
            id: model.id ?? 0,
            mode: model.mode,
            ancillaryKey: model.ancillaryKey,
            status: model.status,
            providerCode: model.providerCode as string,
            airlineCode: model.airlineCode,
            fareTypeName: model.fareTypeName,
            categoryName: model.categoryName as string,
            amount: model.amount,
            unit: model.unit,
            mandatorySelection: model.mandatorySelection,
            priorityOrder: model.priorityOrder,
            titlesByLanguage: model.titlesByLanguage
        };

        if (this.dialogMode === 'add') {
            this.ancillaryService.createAncillary(payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been created successfully.' });
                    this.displayDialog = false;
                    this.loadAncillaries();
                }
            });
        } else if (this.dialogMode === 'edit' && this.selected?.id) {
            this.ancillaryService.updateAncillary(this.selected.id, payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been updated successfully.' });
                    this.displayDialog = false;
                    this.loadAncillaries();
                }
            });
        }
    }

    onMessageProviderChange(): void {
        this.alternativePackageContent = null;
        this.messageTranslations = this.createEmptyMessageTranslations();
        if (!this.selectedMessageProviderCode) {
            return;
        }
        this.messageLoading = true;
        this.ancillaryService.getAlternativePackageContent(this.selectedMessageProviderCode).subscribe({
            next: (content) => {
                this.alternativePackageContent = content;
                this.messageTranslations = {
                    titlesByLanguage: this.normalizeLanguages(content.titlesByLanguage),
                    descriptionsByLanguage: this.normalizeLanguages(content.descriptionsByLanguage)
                };
                this.messageLoading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.messageLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load alternative package message.' });
                this.cd.detectChanges();
            }
        });
    }

    saveAlternativePackageMessage(): void {
        if (!this.selectedMessageProviderCode) {
            return;
        }
        this.messageSaving = true;
        this.ancillaryService
            .updateAlternativePackageContent(this.selectedMessageProviderCode, {
                providerCode: this.selectedMessageProviderCode,
                titlesByLanguage: this.messageTranslations.titlesByLanguage,
                descriptionsByLanguage: this.messageTranslations.descriptionsByLanguage
            })
            .subscribe({
                next: (content) => {
                    this.alternativePackageContent = content;
                    this.messageTranslations = {
                        titlesByLanguage: this.normalizeLanguages(content.titlesByLanguage),
                        descriptionsByLanguage: this.normalizeLanguages(content.descriptionsByLanguage)
                    };
                    this.messageSaving = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Alternative package message has been updated.' });
                    this.cd.detectChanges();
                },
                error: () => {
                    this.messageSaving = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update alternative package message.' });
                    this.cd.detectChanges();
                }
            });
    }

    getFallbackTitle(language: string): string {
        return this.alternativePackageContent?.fallbackTitlesByLanguage?.[language] ?? '';
    }

    getFallbackDescription(language: string): string {
        return this.alternativePackageContent?.fallbackDescriptionsByLanguage?.[language] ?? '';
    }

    onStatusChange(event: any, row: AncillaryItem): void {
        if (!row?.id) return;
        const request: UpdateAncillaryStatusRequest = { status: event.value };
        this.ancillaryService.updateAncillaryStatus(row.id, request).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Status has been updated successfully.' });
                this.loadAncillaries();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status.' });
                this.loadAncillaries();
            }
        });
    }

    applyFilters(): void {
        const text = this.filters.text.trim().toLowerCase();
        this.filteredAncillaries = this.ancillaries.filter((item) => {
            return (
                this.matches(item.providerCode, this.filters.providerCode) &&
                this.matches(item.airlineCode, this.filters.airlineCode) &&
                this.matches(item.fareTypeName, this.filters.fareTypeName) &&
                this.matches(item.categoryName, this.filters.categoryName) &&
                this.matches(item.mode, this.filters.mode) &&
                this.matches(item.status, this.filters.status) &&
                (!text || this.searchBlob(item).includes(text))
            );
        });
    }

    clearFilters(): void {
        this.filters = {
            providerCode: null,
            airlineCode: null,
            fareTypeName: null,
            categoryName: null,
            mode: null,
            status: null,
            text: ''
        };
        this.applyFilters();
    }

    displayType(row: AncillaryItem): string {
        return row.mode === 'BUNDLE_CONTENT' ? 'Bundle Content' : 'Provider Mapping';
    }

    private mapToDialogData(row: AncillaryItem): Partial<AncillaryFormModel> {
        return {
            id: row.id,
            mode: (row.mode ?? 'PROVIDER_SERVICE_MAPPING') as any,
            ancillaryKey: row.ancillaryKey,
            providerCode: row.providerCode,
            airlineCode: row.airlineCode ?? null,
            fareTypeName: row.fareTypeName ?? null,
            categoryName: row.categoryName,
            amount: row.amount ?? null,
            unit: row.unit ?? 'KG',
            mandatorySelection: row.mandatorySelection,
            priorityOrder: row.priorityOrder ?? null,
            status: row.status as any,
            titlesByLanguage: {
                TR: row.titlesByLanguage?.['TR'] ?? '',
                NL: row.titlesByLanguage?.['NL'] ?? '',
                DE: row.titlesByLanguage?.['DE'] ?? '',
                EN: row.titlesByLanguage?.['EN'] ?? ''
            }
        };
    }

    private buildFilterOptions(): void {
        this.providerOptions = this.toOptions(this.ancillaries.map((item) => item.providerCode));
        this.airlineOptions = this.toOptions(this.ancillaries.map((item) => item.airlineCode));
        this.fareOptions = this.toOptions(this.ancillaries.map((item) => item.fareTypeName));
        this.categoryOptions = this.toOptions(this.ancillaries.map((item) => item.categoryName));
    }

    private toOptions(values: Array<string | undefined | null>): SelectItem[] {
        return [...new Set(values.filter(Boolean) as string[])].sort().map((value) => ({ label: value, value }));
    }

    private matches(value: string | undefined | null, filter: string | null): boolean {
        return !filter || value === filter;
    }

    private searchBlob(item: AncillaryItem): string {
        return [
            item.providerCode,
            item.airlineCode,
            item.fareTypeName,
            item.categoryName,
            item.ancillaryKey,
            item.status,
            item.mode,
            item.uiDisplayText,
            item.amount,
            item.unit
        ]
            .filter((value) => value !== undefined && value !== null)
            .join(' ')
            .toLowerCase();
    }

    private normalizeLanguages(values?: { [languageCode: string]: string }): { [languageCode: string]: string } {
        return {
            EN: values?.['EN'] ?? '',
            TR: values?.['TR'] ?? '',
            DE: values?.['DE'] ?? '',
            NL: values?.['NL'] ?? ''
        };
    }

    private createEmptyMessageTranslations(): { titlesByLanguage: { [k: string]: string }; descriptionsByLanguage: { [k: string]: string } } {
        return {
            titlesByLanguage: { EN: '', TR: '', DE: '', NL: '' },
            descriptionsByLanguage: { EN: '', TR: '', DE: '', NL: '' }
        };
    }
}
